import { Component, type ErrorInfo, type ReactNode } from 'react'
import { RouterProvider } from '@tanstack/react-router'
import { TooltipProvider } from '@/components/ui/tooltip/tooltip'
import { Button } from '@/components/ui/button/button'
import { captureTelemetryException } from '@/lib/telemetry'
import { router } from './router'

interface AppErrorBoundaryState {
  hasError: boolean
}

class AppErrorBoundary extends Component<{ children: ReactNode }, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AppErrorBoundary]', error, info.componentStack)
    captureTelemetryException(error, {
      contexts: { react: { componentStack: info.componentStack ?? '' } },
      tags: { boundary: 'app-root' },
    })
  }

  render() {
    if (this.state.hasError) return <CrashScreen />
    return this.props.children
  }
}

function CrashScreen() {
  return (
    <div
      data-visual-app-crash
      className="flex min-h-screen items-center justify-center bg-background"
    >
      <div className="text-center">
        <p className="text-8xl font-bold text-grey-200">!</p>
        <h1 className="mt-4 text-xl font-semibold text-foreground">
          Something went wrong
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          The app hit an unexpected error. Reloading usually fixes it.
        </p>
        <Button
          type="button"
          size="lg"
          onClick={() => window.location.reload()}
          className="mt-6"
        >
          Reload
        </Button>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <AppErrorBoundary>
      <TooltipProvider delay={150}>
        <RouterProvider router={router} />
      </TooltipProvider>
    </AppErrorBoundary>
  )
}
